import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import {
  FetchEventTraceQuery,
  type IngestionEvent,
} from '@/lib/api/system/ingestion-observability';
import { cn } from '@/lib/utils';
import { useMemo } from 'react';
import { ArrowDown, Clock, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  OUTCOME_COLORS,
  getGroupMeta,
  formatDuration,
} from '../constants/stage-categories';
import { useIngestionManifest } from '../hooks/useIngestionManifest';
import { useSite } from '@/hooks/siteHooks';
import { useCustomer } from '@/features/Customers/hooks/useCustomer';
import { routes } from '@/lib/routing';

interface IngestionEventTraceProps {
  tenantId: string;
  correlationId: string;
  /** Optional timestamp from the originating log row, used as a hint to narrow the Cloud Logging query window. */
  timestampHint?: string;
}

const METADATA_FIELD_LABELS: Partial<Record<keyof IngestionEvent, string>> = {
  stopReason: 'Stop Reason',
  reason: 'Reason',
  metadataError: 'Error',
  metadataEventType: 'Event Type',
  metadataPredictedStatus: 'Predicted Status',
  metadataVendor: 'Vendor',
  metadataTriggerType: 'Trigger Type',
};

// Fields shown per-step (entity IDs are shown at trace level instead)
const CORE_FIELDS: { key: keyof IngestionEvent; label: string }[] = [
  { key: 'stopReason', label: 'Stop Reason' },
  { key: 'metadataVendor', label: 'Vendor' },
  { key: 'metadataEventType', label: 'Event Type' },
  { key: 'metadataError', label: 'Error' },
  { key: 'metadataPredictedStatus', label: 'Predicted Status' },
  { key: 'metadataTriggerType', label: 'Trigger Type' },
];

export function IngestionEventTrace({
  tenantId,
  correlationId,
  timestampHint,
}: IngestionEventTraceProps) {
  const { data, isLoading, error } = useQuery(
    FetchEventTraceQuery(tenantId, correlationId, timestampHint)
  );
  const { getGroupForStage, stageDisplayNames, stageVisibility, stageOrder, isSuperAdmin } = useIngestionManifest();

  const visibleData = useMemo(() => {
    if (!data) return [];
    return data
      .filter((event) => {
        const visibility = stageVisibility.get(event.stage);
        if (visibility === 'erroronly' && event.outcome !== 'Failed') return false;
        if (visibility === 'internal' && !isSuperAdmin) return false;
        return true;
      })
      .sort((a, b) => (stageOrder.get(a.stage) ?? 9999) - (stageOrder.get(b.stage) ?? 9999));
  }, [data, stageVisibility, stageOrder, isSuperAdmin]);

  // Extract constant entity IDs from across all trace events
  const traceContext = useMemo(() => {
    if (!data || data.length === 0) return null;
    let projectId: string | undefined;
    let companyId: string | undefined; // actual customer/company ID (enriched field)
    let tenantId: string | undefined;
    let vpcId: string | undefined;
    let assetId: string | undefined;
    let monitoringEventId: string | undefined;
    for (const e of data) {
      projectId ??= e.projectId;
      companyId ??= e.companyId; // from BQ enrichment — NOT tenantId
      tenantId ??= e.tenantId;
      vpcId ??= e.videoProcessorConfigId;
      assetId ??= e.assetId;
      monitoringEventId ??= e.monitoringEventId;
    }
    return { projectId, companyId, tenantId, vpcId, assetId, monitoringEventId };
  }, [data]);

  // Conditionally fetch entity names when we have the required IDs
  const canFetchSite = !!(traceContext?.companyId && traceContext?.projectId);
  const canFetchCustomer = !!traceContext?.companyId;

  const { data: siteData } = useSite({
    siteId: traceContext?.projectId ?? '',
    companyId: traceContext?.companyId ?? '',
    enabled: canFetchSite,
  });

  const { data: customerData } = useCustomer(traceContext?.companyId ?? '');

  const traceTiming = useMemo(() => {
    if (!visibleData || visibleData.length === 0) return null;
    const startTime = new Date(visibleData[0].timestamp);
    const endTime = new Date(visibleData[visibleData.length - 1].timestamp);
    const durationMs = endTime.getTime() - startTime.getTime();
    return { startTime, endTime, durationMs };
  }, [visibleData]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-11" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-400">
        Failed to load trace: {error.message}
      </div>
    );
  }

  if (!visibleData || visibleData.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-white/10 bg-white/5 p-4 text-center text-sm text-white/30">
        No logs found for this correlation ID
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <span className="text-xs text-white/40">Correlation</span>
        <code className="rounded bg-white/5 px-2 py-0.5 font-mono text-xs text-white/70">
          {correlationId}
        </code>
      </div>

      {/* Entity context — constant IDs extracted from across all trace events */}
      {traceContext && (
        <div className="mb-3 space-y-1 rounded-lg bg-white/[0.03] px-3 py-2 text-[11px]">
          {traceContext.companyId && (
            <div className="flex items-center gap-1.5">
              <span className="w-[70px] shrink-0 text-white/40">Company</span>
              {customerData?.name ? (
                <Link
                  to={routes.customer({ customerId: traceContext.companyId })}
                  className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors"
                >
                  <span>{customerData.name}</span>
                  <ExternalLink className="h-2.5 w-2.5" />
                </Link>
              ) : (
                <code className="font-mono text-white/40">{traceContext.companyId}</code>
              )}
            </div>
          )}
          {traceContext.projectId && (
            <div className="flex items-center gap-1.5">
              <span className="w-[70px] shrink-0 text-white/40">Site</span>
              {siteData?.name && traceContext.companyId ? (
                <Link
                  to={routes.customerSite({ customerId: traceContext.companyId, siteId: traceContext.projectId })}
                  className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors"
                >
                  <span>{siteData.name}</span>
                  <ExternalLink className="h-2.5 w-2.5" />
                </Link>
              ) : (
                <code className="font-mono text-white/40">{traceContext.projectId}</code>
              )}
            </div>
          )}
          {traceContext.tenantId && (
            <div className="flex items-center gap-1.5">
              <span className="w-[70px] shrink-0 text-white/40">Tenant</span>
              <code className="font-mono text-white/40">{traceContext.tenantId}</code>
            </div>
          )}
          {traceContext.vpcId && (
            <div className="flex items-center gap-1.5">
              <span className="w-[70px] shrink-0 text-white/40">Camera</span>
              <code className="font-mono text-white/40">{traceContext.vpcId}</code>
            </div>
          )}
          {traceContext.assetId && (
            <div className="flex items-center gap-1.5">
              <span className="w-[70px] shrink-0 text-white/40">Asset</span>
              <code className="font-mono text-white/40">{traceContext.assetId}</code>
            </div>
          )}
          {traceContext.monitoringEventId && (
            <div className="flex items-center gap-1.5">
              <span className="w-[70px] shrink-0 text-white/40">Mon. Event</span>
              <code className="font-mono text-white/40">{traceContext.monitoringEventId}</code>
            </div>
          )}
        </div>
      )}

      {/* Start / End / Duration */}
      {traceTiming && (
        <div className="mb-3 flex items-center gap-3 rounded-lg bg-white/[0.03] px-3 py-2 text-[11px]">
          <Clock className="h-3.5 w-3.5 shrink-0 text-white/30" />
          <div className="flex items-center gap-1.5">
            <span className="text-white/40">Start</span>
            <span className="font-mono text-white/70">
              {traceTiming.startTime.toLocaleString(undefined, {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
              })}
            </span>
          </div>
          <span className="text-white/20">&rarr;</span>
          <div className="flex items-center gap-1.5">
            <span className="text-white/40">End</span>
            <span className="font-mono text-white/70">
              {traceTiming.endTime.toLocaleString(undefined, {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
              })}
            </span>
          </div>
          <span className="text-white/20">&middot;</span>
          <span className="font-mono font-medium text-white/80">
            {formatDuration(traceTiming.durationMs)}
          </span>
        </div>
      )}

      {/* Direction indicator */}
      <div className="mb-2 flex items-center gap-1.5 text-[10px] text-white/30">
        <ArrowDown className="h-3 w-3" />
        <span>Pipeline direction</span>
      </div>

      <Accordion type="single" collapsible className="space-y-0.5">
        {visibleData.map((event, index) => (
          <TraceStep
            key={index}
            event={event}
            step={index + 1}
            isLast={index === visibleData.length - 1}
            groupKey={getGroupForStage(event.stage)}
            displayName={stageDisplayNames.get(event.stage) ?? event.stage}
            isInternal={stageVisibility.get(event.stage) === 'internal'}
          />
        ))}
      </Accordion>
    </div>
  );
}

function TraceStep({
  event,
  step,
  isLast,
  groupKey,
  displayName,
  isInternal,
}: {
  event: IngestionEvent;
  step: number;
  isLast: boolean;
  groupKey: string;
  displayName: string;
  isInternal: boolean;
}) {
  const meta = getGroupMeta(groupKey);
  const Icon = meta.icon;
  const outcomeStyle = OUTCOME_COLORS[event.outcome] ?? OUTCOME_COLORS.Skipped;

  // Non-ID metadata fields for inline display
  const inlineMetadata = (Object.keys(METADATA_FIELD_LABELS) as (keyof IngestionEvent)[])
    .map((key) => {
      const value = event[key];
      if (!value || typeof value !== 'string') return null;
      return { label: METADATA_FIELD_LABELS[key]!, value };
    })
    .filter(Boolean) as { label: string; value: string }[];

  const coreEntries = CORE_FIELDS.map((f) => ({
    label: f.label,
    value: event[f.key] as string | undefined,
  })).filter((e) => e.value);

  const allEntries = coreEntries.map((e) => [e.label, e.value!] as const);

  return (
    <div className={cn('relative', isInternal && 'opacity-50')}>
      {/* Connecting line between steps */}
      {!isLast && (
        <div className="absolute left-[15px] top-[32px] bottom-0 w-px bg-white/[0.06]" />
      )}

      <AccordionItem
        value={`stage-${step}`}
        className={cn(
          'relative border-none rounded-lg overflow-hidden border-l-2',
          outcomeStyle.border
        )}
      >
        <AccordionTrigger className="py-2 px-3 hover:no-underline hover:bg-white/[0.03] font-normal text-sm">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            {/* Step number — colored by outcome */}
            <span
              title={event.outcome}
              className={cn(
                'shrink-0 flex h-[26px] w-[26px] items-center justify-center rounded-full text-[10px] font-semibold ring-1',
                outcomeStyle.bg,
                outcomeStyle.text,
                outcomeStyle.ring,
              )}
            >
              {step}
            </span>

            {/* Group icon — colored by outcome */}
            <Icon className={cn('h-3.5 w-3.5 shrink-0', outcomeStyle.text)} />

            {/* Stage display name */}
            <span className="font-medium text-white/90 truncate">
              {displayName}
            </span>

            {/* Type badge */}
            {event.type && (
              <span className="inline-flex shrink-0 items-center rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] text-white/50">
                {event.type}
              </span>
            )}

            {/* Internal badge */}
            {isInternal && (
              <span className="inline-flex shrink-0 items-center rounded bg-white/[0.04] px-1.5 py-0.5 text-[10px] italic text-white/30">
                internal
              </span>
            )}

            {/* Inline metadata fields */}
            {inlineMetadata.length > 0 && (
              <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                {inlineMetadata.map(({ label, value }) => (
                  <span
                    key={label}
                    title={`${label}: ${value}`}
                    className="inline-flex shrink-0 max-w-[160px] items-center gap-1 rounded bg-white/[0.05] px-1.5 py-0.5 text-[10px] text-white/40"
                  >
                    <span className="text-white/25">{label}</span>
                    <span className="truncate text-white/55">{value}</span>
                  </span>
                ))}
              </div>
            )}

            {/* Timestamp */}
            <span className="ml-auto shrink-0 pl-2 font-mono text-[10px] text-white/30">
              {new Date(event.timestamp).toLocaleTimeString()}
            </span>
          </div>
        </AccordionTrigger>

        <AccordionContent className="px-3 pb-3 pl-[46px]">
          <div className="space-y-2">
            {/* Group label */}
            <span className={cn('text-[10px] font-medium', outcomeStyle.text)}>
              {meta.label}
            </span>

            {event.stopReason && (
              <div className="text-xs text-amber-400/80">
                <span className="text-amber-400/50">Stop Reason: </span>
                {event.stopReason}
              </div>
            )}
            {event.reason && (
              <div className="text-xs text-white/60">
                <span className="text-white/30">Reason: </span>
                {event.reason}
              </div>
            )}
            {allEntries.length > 0 && (
              <table className="w-full text-xs">
                <tbody className="divide-y divide-white/5">
                  {allEntries.map(([key, value]) => (
                    <tr key={key}>
                      <td className="py-1 pr-4 align-top whitespace-nowrap text-white/30">
                        {key}
                      </td>
                      <td className="py-1 break-all text-white/60 font-mono">
                        {value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </div>
  );
}
